import {
  Component,
  forwardRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Editor } from '@tiptap/core';
import { RtfEditorService } from './rtf-editor.service';

import {
  Copy,
  Bold,
  Italic,
  List,
  ListOrdered,
  Undo,
  Redo,
  Table as Table2,
  Rows2,
  Columns2,
  Trash,
  Check,
  Underline
} from 'lucide-angular';

@Component({
  standalone: false,
  selector: 'cva-rtf-editor',
  templateUrl: './rtf-editor.component.html',
  styleUrl: './rtf-editor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RtfEditorComponent),
      multi: true,
    },
  ],

})
export class RtfEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
  editor!: Editor;
  copiedType: 'plain' | 'formatted' | null = null;

  // Icons
  readonly Copy = Copy;
  readonly Bold = Bold;
  readonly Italic = Italic;
  readonly List = List;
  readonly ListOrdered = ListOrdered;
  readonly Undo = Undo;
  readonly Redo = Redo;
  readonly Table2 = Table2;
  readonly Rows = Rows2;
  readonly Columns = Columns2;
  readonly Trash = Trash;
  readonly Check = Check;
  readonly Underline = Underline;

  private onChange: (value: string) => void = () => { };
  private onTouched = () => { };

  private userPlacedCursor = false;
  constructor(private rtfService: RtfEditorService) { }

  ngOnInit(): void {
    this.editor = new Editor({
      extensions: this.rtfService.getExtensions(),
      editorProps: {
        attributes: {
          class:
            'prose max-w-none focus:outline-none min-h-[200px]  focus:border-none',
        },
        handleDOMEvents: {
          click: (_, event) => this.handleCitation(event),
        },
      },
      onUpdate: ({ editor }) => {
        this.onChange(editor.getHTML());
      },
      onBlur: () => this.onTouched(),
      onFocus: () => (this.userPlacedCursor = true),
    });
  }

  writeValue(value: string): void {
    if (this.editor && value !== this.editor.getHTML()) {
      this.editor.commands.setContent(value || '', false);
      this.userPlacedCursor = false;
    }
  }

  insertSpeechText(text: string): void {
    if (!text?.trim()) return;
    if (!this.userPlacedCursor) {
      this.editor.commands.focus('end'); // never clicked: append at end
    }
    const { from } = this.editor.state.selection;
    const prev = this.editor.state.doc.textBetween(Math.max(0, from - 1), from);
    const out = (prev && !/\s/.test(prev) ? ' ' : '') + text.trim() + ' ';
    this.editor.chain().focus().insertContent({ type: 'text', text: out }).run();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.editor?.setEditable(!isDisabled);
  }

  handleCitation(event: Event): boolean {
    const target = event.target as HTMLElement;

    const ids = target.dataset['sourceIds']
      ?.split(',')
      .map(id => id.trim());

    if (ids?.length) {
      console.log('Citation clicked:', ids);
      return true;
    }

    return false;
  }

  getHtml(): string {
    return this.editor.getHTML();
  }

  getRefinementContext(): { content: string; target: string } {
    const { from, to } = this.editor.state.selection;
    return {
      content: this.editor.getText({ blockSeparator: '\n' }),
      target: this.editor.state.doc.textBetween(from, to, '\n'),
    };
  }

  onCopy(): void {
    const text = this.editor
      .getText()
      .replace(/\u00A0/g, ' ')     // non-breaking spaces → normal space
      .replace(/[ \t]+/g, ' ')     // collapse multiple spaces
      .replace(/\n{3,}/g, '\n\n')  // max 2 newlines
      .trim();

    navigator.clipboard.writeText(text).then(() => {
      this.showCopied('plain');
    });
  }

  onClipboarbCopy(): void {
    const html = this.editor.getHTML();
    let text = this.editor.getText();
    text = text.replace(/\n{3,}/g, '\n\n').trim();

    navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]).then(() => {
      this.showCopied('formatted');
    });
  }

  showCopied(type: 'plain' | 'formatted') {
    this.copiedType = type;
    setTimeout(() => (this.copiedType = null), 1000);
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }
}
